import React from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Settings as SettingsIcon, User as UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function initials(name = "") {
  const parts = String(name || "").trim().split(/\s+/).slice(0, 2);
  if (!parts[0]) return "IC";
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "IC";
}

export default function UserMenu() {
  const t = useT();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const name = user?.name || user?.display_name || user?.email || "";
  const email = user?.email || "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="user-menu-trigger"
        className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-border bg-white hover:shadow-card transition"
      >
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-sage-100 text-sage-700 font-semibold text-sm">
          {initials(name)}
        </span>
        <span className="hidden md:inline text-sm font-medium text-ink-900 max-w-[140px] truncate">
          {name || email || "You"}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 rounded-xl">
        <DropdownMenuLabel className="font-heading text-ink-900">
          <div className="truncate">{name || "Signed in"}</div>
          {email && <div className="text-xs text-ink-500 font-sans font-normal truncate">{email}</div>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-profile"
          onSelect={() => nav("/app/settings")}
          className="rounded-lg"
        >
          <UserIcon className="mr-2 h-4 w-4" /> {t("settings.profile")}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-settings"
          onSelect={() => nav("/app/settings")}
          className="rounded-lg"
        >
          <SettingsIcon className="mr-2 h-4 w-4" /> {t("common.settings")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-signout"
          onSelect={async () => {
            await logout();
            nav("/login", { replace: true });
          }}
          className="rounded-lg text-rust-600 focus:text-rust-600"
        >
          <LogOut className="mr-2 h-4 w-4" /> {t("common.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
